import { useMemo, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { useWholesaleAnalytics } from "@/hooks/use-wholesale";
import { cn } from "@/lib/utils";
import { fmtKm } from "@/lib/wholesale-format";
import { ANALYTICS_PERIODS, analyticsRange, fmtRange, type AnalyticsPeriod } from "@/lib/wholesale-analytics-period";
import type { AnalyticsKpi } from "@/services/wholesale-analytics";
import { Section, Segmented, Delta, fmtInt } from "@/components/wholesale/analytics/parts";
import { TrendChart } from "@/components/wholesale/analytics/trend-chart";
import { CustomersSection, DormantSection } from "@/components/wholesale/analytics/customers-section";
import { ProductsSection } from "@/components/wholesale/analytics/products-section";
import { CategoriesSection } from "@/components/wholesale/analytics/categories-section";

const KPIS: { key: keyof AnalyticsKpi; label: string; fmt: (n: number) => string }[] = [
  { key: "revenue", label: "Promet", fmt: fmtKm },
  { key: "orders", label: "Narudžbe", fmt: fmtInt },
  { key: "units", label: "Komada", fmt: fmtInt },
  { key: "avgOrder", label: "Prosj. narudžba", fmt: fmtKm },
  { key: "customers", label: "Aktivni kupci", fmt: fmtInt },
];

/** Ključne brojke kao jedna traka: ćelije dijele okvir, uz svaku promjena i vrijednost prije.
 *  Bez narudžbi u prethodnom periodu red poređenja otpada — „novo, prije 0" ne govori ništa. */
function KpiStrip({ current, previous, compare }: { current: AnalyticsKpi; previous: AnalyticsKpi; compare: boolean }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-foreground/10 ring-1 ring-foreground/10">
      {KPIS.map((k, i) => (
        <div key={k.key} className={cn("bg-background px-4 py-3.5 flex flex-col gap-1.5 min-w-0", i === KPIS.length - 1 && "col-span-2 sm:col-span-1")}>
          <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{k.label}</span>
          <span className="text-lg sm:text-xl font-medium tabular-nums truncate leading-none">{k.fmt(current[k.key])}</span>
          {compare && (
            <span className="flex items-baseline gap-1.5 text-[10px] text-muted-foreground tabular-nums">
              <Delta current={current[k.key]} previous={previous[k.key]} />
              <span className="truncate">prije {k.fmt(previous[k.key])}</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function WholesaleAnalyticsPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const range = useMemo(() => analyticsRange(period, custom), [period, custom]);
  const { data, isLoading, isFetching, error } = useWholesaleAnalytics(range.from, range.to);
  const compare = (data?.kpi.previous.orders ?? 0) > 0;

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-medium">Analitika veleprodaje</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
            {fmtRange(range.from, range.to)}
            {data && (compare
              ? <> · poređenje s {fmtRange(data.previous.from, data.previous.to)}</>
              : <> · bez poređenja: {fmtRange(data.previous.from, data.previous.to)} nema potvrđenih narudžbi</>)}
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <Segmented label="Period" value={period} options={ANALYTICS_PERIODS} onChange={setPeriod} />
          {period === "custom" && (
            <div className="flex items-center gap-1">
              <DatePicker value={custom.from} onChange={(from) => setCustom((c) => ({ ...c, from }))} placeholder="Od" className="h-7 text-xs w-28" />
              <DatePicker value={custom.to} onChange={(to) => setCustom((c) => ({ ...c, to }))} placeholder="Do" className="h-7 text-xs w-28" />
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : error ? (
        <div className="ring-1 ring-destructive/30 p-6 text-center text-xs text-destructive">
          Analitika nije učitana: {(error as Error).message}
        </div>
      ) : data ? (
        <div className={cn("flex flex-col gap-4 transition-opacity", isFetching && "opacity-60")} aria-busy={isFetching}>
          <KpiStrip current={data.kpi.current} previous={data.kpi.previous} compare={compare} />

          <Section title="Promet kroz vrijeme">
            <TrendChart points={data.trend} granularity={data.granularity} compare={compare} />
          </Section>

          {data.kpi.current.orders === 0 && (
            <p className="ring-1 ring-foreground/10 px-3 py-4 text-xs text-muted-foreground">
              U ovom periodu nema potvrđenih narudžbi. Izaberi duži period ili prilagođene datume.
            </p>
          )}

          <CustomersSection customers={data.customers} compare={compare} />
          {data.dormant.length > 0 && <DormantSection dormant={data.dormant} />}

          <ProductsSection products={data.products} compare={compare} />
          <CategoriesSection categories={data.categories} compare={compare} />
        </div>
      ) : null}
    </div>
  );
}
